import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Spinner, Alert, Carousel } from 'react-bootstrap';
import { productHuntService } from '../services/productHuntService';
import AgentDetailModal from './AgentDetailModal';
import { MAIN_CATEGORIES, TOPIC_TO_CATEGORY_MAP } from '../config/categories';
import { FEATURES } from '../config/featureFlags';

const AgentList = () => {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [endCursor, setEndCursor] = useState(null);
    const [category, setCategory] = useState('all');
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        fetchAgentsFromProductHunt();
    }, []);

    const fetchAgentsFromProductHunt = async (cursor = null) => {
        try {
            const data = await productHuntService.searchAITools(cursor);
            
            // Check if data has the expected structure
            if (!data || !data.posts || !data.posts.edges) {
                // console.error('Invalid API response:', data);
                setError('Failed to load AI tools. Please check your API configuration.');
                setLoading(false);
                return;
            }
            
            const newAgents = data.posts.edges;
            
            if (cursor) {
                // Append new agents to existing list
                setAgents(prevAgents => [...prevAgents, ...newAgents]);
            } else {
                // Reset list with new agents
                setAgents(newAgents);
            }
            
            // Update pagination info
            setHasNextPage(data.posts.pageInfo.hasNextPage);
            setEndCursor(data.posts.pageInfo.endCursor);
            
            setLoading(false);
            setLoadingMore(false);
        } catch (error) {
            // console.error('Error fetching agents:', error);
            setError(error.message || 'Failed to load AI tools');
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadMore = () => {
        if (hasNextPage && !loadingMore) {
            setLoadingMore(true);
            fetchAgentsFromProductHunt(endCursor);
        }
    };

    const getMediaUrls = (media) => {
        if (!media) return [];
        return media.filter(item => item.url).map(item => item.url);
    };

    const getTopics = (topics) => {
        if (!topics?.edges) return [];
        return topics.edges.map(edge => edge.node.name);
    };

    const getMainCategory = (topics) => {
        if (!topics?.edges) return null;
        
        // Try to find the first matching main category
        for (const topic of topics.edges) {
            const mainCategory = TOPIC_TO_CATEGORY_MAP[topic.node.name];
            if (mainCategory) return mainCategory;
        }
        
        // Default to AI & Machine Learning if no match found
        return MAIN_CATEGORIES.AI_TOOLS;
    };

    const filteredAgents = agents.filter(item => {
        const mainCategory = getMainCategory(item.node.topics);
        return category === 'all' || mainCategory === category;
    });

    if (loading) {
        return (
            <Container className="text-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="text-center py-5">
                <Alert variant="danger">
                    Error loading agents: {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container>
            <div className="filter-section mb-4">
                <Row className="align-items-center">
                    <Col md={FEATURES.SHOW_REFRESH ? 6 : 12} className="mb-3 mb-md-0">
                        <Form.Select 
                            value={category} 
                            onChange={(e) => setCategory(e.target.value)}
                            className="category-filter"
                        >
                            <option value="all">All Categories</option>
                            {Object.values(MAIN_CATEGORIES).map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    {FEATURES.SHOW_REFRESH && (
                        <Col md={6} className="text-md-end">
                            <Button variant="primary" onClick={fetchAgentsFromProductHunt}>
                                Refresh Data
                            </Button>
                        </Col>
                    )}
                </Row>
            </div>

            <Row>
                        {filteredAgents.map((item) => (
                            <Col key={item.node.id} sm={12} md={6} lg={4} className="mb-4">
                                <Card className="h-100">
                                    {item.node.media && item.node.media.length > 0 && (
                                        <Carousel interval={null} className="card-carousel">
                                            {getMediaUrls(item.node.media).map((url, index) => (
                                                <Carousel.Item key={index}>
                                                    <img
                                                        className="d-block w-100"
                                                        src={url}
                                                        alt={`${item.node.name} - ${index + 1}`}
                                                        style={{ height: '200px', objectFit: 'cover' }}
                                                    />
                                                </Carousel.Item>
                                            ))}
                                        </Carousel>
                                    )}
                                    <Card.Body>
                                        <Card.Title>{item.node.name}</Card.Title>
                                        <Card.Text>{item.node.tagline}</Card.Text>
                                        <div className="mb-2">
                                            <Badge 
                                                bg="primary" 
                                                className="me-2 mb-2"
                                            >
                                                {getMainCategory(item.node.topics)}
                                            </Badge>
                                            {getTopics(item.node.topics).map((topic, index) => (
                                                <Badge 
                                                    key={index} 
                                                    bg="info" 
                                                    className="me-1 mb-1"
                                                >
                                                    {topic}
                                                </Badge>
                                            ))}
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center">
                                            {FEATURES.SHOW_VOTES && (
                                                <Badge bg="secondary">
                                                    Votes: {item.node.votesCount}
                                                </Badge>
                                            )}
                                            {!FEATURES.SHOW_VOTES && <div />}
                                            {item.node.description && (
                                                <Button 
                                                    variant="outline-secondary" 
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedAgent(item);
                                                        setShowModal(true);
                                                    }}
                                                >
                                                    More Info
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                    <Card.Footer className="text-muted">
                                        <small>
                                            Created: {new Date(item.node.createdAt).toLocaleDateString()}
                                        </small>
                                        {item.node.website && (
                                            <Button 
                                                variant="link" 
                                                href={item.node.website} 
                                                target="_blank"
                                                className="float-end"
                                            >
                                                Visit Website
                                            </Button>
                                        )}
                                    </Card.Footer>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                    
                    {hasNextPage && (
                        <div className="text-center my-4">
                            <Button 
                                variant="outline-primary" 
                                onClick={loadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Loading more...
                                    </>
                                ) : (
                                    'Load More'
                                )}
                            </Button>
                        </div>
                    )}

            <AgentDetailModal 
                show={showModal}
                onHide={() => setShowModal(false)}
                agent={selectedAgent}
            />
        </Container>
    );
};

export default AgentList; 